import { useState, useEffect, useCallback, useRef } from "react";
import { performanceCycleApi } from "../api/performanceCycleApi";

export const usePerformanceCycles = (filters = undefined) => {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastKeyRef = useRef(null);
  const inFlightRef = useRef(false);

  const fetchCycles = useCallback(async () => {
    try {
      const key = JSON.stringify(filters || {});
      if (inFlightRef.current) return;
      if (lastKeyRef.current === key && cycles?.data?.cycles?.length) return;
      lastKeyRef.current = key;
      inFlightRef.current = true;
      setLoading(true);
      setError(null);
      const response = await performanceCycleApi.getCycles(filters || {});

      // Handle different response formats
      let cycleData = [];
      if (response && response.data && response.data.cycles) {
        // API response structure: { responseCode, responseMessage, data: { cycles: [...] } }
        cycleData = Array.isArray(response.data.cycles)
          ? response.data.cycles
          : [];
      } else if (response && response.data && Array.isArray(response.data)) {
        // Direct array in data
        cycleData = response.data;
      } else if (Array.isArray(response)) {
        // Direct array response
        cycleData = response;
      }

      // Store the full response structure for the component
      setCycles(response);
    } catch (err) {
      setError(err.message || "Failed to fetch performance cycles");
      // Set empty structure on error to prevent map errors
      setCycles({
        responseCode: 500,
        responseMessage: "Error",
        data: {
          cycles: [],
          pagination: {},
        },
      });
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [filters ? JSON.stringify(filters) : "{}"]); // stable dependency key

  const createCycle = useCallback(async (cycleData) => {
    try {
      setLoading(true);
      // keep fetch error state separate; do not flip global error on mutations
      const response = await performanceCycleApi.createCycle(cycleData);

      // Update the cycles array in the response structure
      setCycles((prev) => {
        const prevCycles = prev?.data?.cycles || [];
        return {
          ...prev,
          data: {
            ...prev.data,
            cycles: [...prevCycles, response.data],
          },
        };
      });

      return response.data;
    } catch (err) {
      // surface error to caller without toggling global fetch error UI
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCycle = useCallback(async (cycleId, cycleData) => {
    try {
      setLoading(true);
      // avoid setting global error for mutations
      const response = await performanceCycleApi.updateCycle(
        cycleId,
        cycleData
      );

      // Update the cycles array in the response structure
      setCycles((prev) => {
        const prevCycles = prev?.data?.cycles || [];
        return {
          ...prev,
          data: {
            ...prev.data,
            cycles: prevCycles.map((cycle) =>
              cycle.id === cycleId ? response.data : cycle
            ),
          },
        };
      });

      return response.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteCycle = useCallback(async (cycleId) => {
    try {
      setLoading(true);
      // avoid setting global error for mutations
      await performanceCycleApi.deleteCycle(cycleId);

      // Update the cycles array in the response structure
      setCycles((prev) => {
        const prevCycles = prev?.data?.cycles || [];
        return {
          ...prev,
          data: {
            ...prev.data,
            cycles: prevCycles.filter((cycle) => cycle.id !== cycleId),
          },
        };
      });
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const startCycle = useCallback(async (cycleId) => {
    try {
      setLoading(true);
      // avoid setting global error for mutations
      const response = await performanceCycleApi.startCycle(cycleId);

      // Update the cycles array in the response structure
      setCycles((prev) => {
        const prevCycles = prev?.data?.cycles || [];
        return {
          ...prev,
          data: {
            ...prev.data,
            cycles: prevCycles.map((cycle) =>
              cycle.id === cycleId ? { ...cycle, status: "active" } : cycle
            ),
          },
        };
      });

      return response.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const endCycle = useCallback(async (cycleId) => {
    try {
      setLoading(true);
      // avoid setting global error for mutations
      const response = await performanceCycleApi.endCycle(cycleId);

      // Update the cycles array in the response structure
      setCycles((prev) => {
        const prevCycles = prev?.data?.cycles || [];
        return {
          ...prev,
          data: {
            ...prev.data,
            cycles: prevCycles.map((cycle) =>
              cycle.id === cycleId ? { ...cycle, status: "completed" } : cycle
            ),
          },
        };
      });

      return response.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const launchCycle = useCallback(async (cycleId, data = {}) => {
    try {
      setLoading(true);
      // avoid setting global error for mutations
      const response = await performanceCycleApi.launchCycle(cycleId, data);

      // Update status locally to 'launched'
      setCycles((prev) => {
        const prevCycles = prev?.data?.cycles || [];
        return {
          ...prev,
          data: {
            ...prev.data,
            cycles: prevCycles.map((cycle) =>
              cycle.id === cycleId ? { ...cycle, status: 'launched' } : cycle
            ),
          },
        };
      });

      return response.data || response;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const cloneCycle = useCallback(async (cycleId) => {
    try {
      setLoading(true);
      // Fetch the source cycle fresh to avoid shape issues
      let source = null;
      try {
        const byId = await performanceCycleApi.getCycleById(cycleId);
        source = byId?.data || byId;
      } catch (_) {
        // fallback to local cache
        const currentCycles = cycles?.data?.cycles || [];
        source = currentCycles.find((c) => c.id === cycleId);
      }

      if (!source) {
        throw new Error("Cycle not found");
      }

      // Use future-safe dates so backend validation passes
      const today = new Date();
      const futureStartDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const futureEndDate = new Date(
        futureStartDate.getTime() + 30 * 24 * 60 * 60 * 1000
      );

      const cloneData = {
        cycleName: `${source.cycleName || source.name} (Copy)`,
        description: source.description || "",
        startDate: futureStartDate.toISOString(),
        endDate: futureEndDate.toISOString(),
        status: "draft",
      };

      const response = await performanceCycleApi.createCycle(cloneData);

      // Update the cycles array in the response structure
      setCycles((prev) => {
        const prevCycles = prev?.data?.cycles || [];
        return {
          ...prev,
          data: {
            ...prev.data,
            cycles: [...prevCycles, response.data],
          },
        };
      });

      return response.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCycles(), 50);
    return () => clearTimeout(t);
  }, [fetchCycles]);

  return {
    cycles,
    loading,
    error,
    fetchCycles,
    createCycle,
    updateCycle,
    deleteCycle,
    startCycle,
    endCycle,
    cloneCycle,
    launchCycle,
    // React Query compatibility
    refetch: fetchCycles,
    data: cycles, // This is what the component expects as cyclesResponse
    isLoading: loading,
    isError: !!error,
  };
};

// Export individual hooks for direct import
export const useCreatePerformanceCycle = () => {
  const { createCycle, loading, error } = usePerformanceCycles();
  return {
    createCycle,
    loading,
    error,
    // React Query compatibility
    mutateAsync: createCycle,
    isPending: loading,
    isError: !!error,
  };
};

export const useDeletePerformanceCycle = () => {
  const { deleteCycle, loading, error } = usePerformanceCycles();
  return {
    deleteCycle,
    loading,
    error,
    // React Query compatibility
    mutateAsync: deleteCycle,
    isPending: loading,
    isError: !!error,
  };
};

export const useUpdatePerformanceCycle = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateCycle = useCallback(async (cycleId, cycleData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await performanceCycleApi.updateCycle(
        cycleId,
        cycleData
      );
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseMessage ||
        err.message ||
        "Failed to update performance cycle";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateCycle,
    loading,
    error,
    // React Query compatibility
    mutateAsync: updateCycle,
    isPending: loading,
    isError: !!error,
  };
};

export const usePerformanceCycle = (cycleId) => {
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCycle = useCallback(async () => {
    if (!cycleId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await performanceCycleApi.getCycleById(cycleId);

      // Handle different response formats
      let cycleData = null;
      if (response && response.data) {
        cycleData = response.data;
      } else if (response) {
        cycleData = response;
      }

      setCycle(cycleData);
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseMessage ||
        err.message ||
        "Failed to fetch performance cycle";
      setError(errorMessage);
      setCycle(null);
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  useEffect(() => {
    fetchCycle();
  }, [fetchCycle]);

  return {
    cycle,
    loading,
    error,
    refetch: fetchCycle,
    // React Query compatibility
    data: cycle,
    isLoading: loading,
    isError: !!error,
  };
};

export const useClonePerformanceCycle = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cloneCycle = useCallback(async (cycleId) => {
    try {
      setLoading(true);
      setError(null);

      // First get the cycle to clone
      const response = await performanceCycleApi.getCycleById(cycleId);
      const cycleToClone = response.data || response;

      if (!cycleToClone) {
        throw new Error("Cycle not found");
      }

      // Create clone data with future dates
      const today = new Date();
      const futureStartDate = new Date(today.getTime() + 24 * 60 * 60 * 1000); // Tomorrow
      const futureEndDate = new Date(
        futureStartDate.getTime() + 30 * 24 * 60 * 60 * 1000
      ); // 30 days later

      const cloneData = {
        cycleName: `${cycleToClone.cycleName} (Copy)`,
        description: cycleToClone.description,
        startDate: futureStartDate.toISOString(),
        endDate: futureEndDate.toISOString(),
        status: "draft",
      };

      const createResponse = await performanceCycleApi.createCycle(cloneData);
      return createResponse.data;
    } catch (err) {
      setError(err.message || "Failed to clone performance cycle");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    cloneCycle,
    loading,
    error,
    // React Query compatibility
    mutateAsync: cloneCycle,
    isPending: loading,
    isError: !!error,
  };
};

export const useLaunchPerformanceCycle = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const launchCycle = useCallback(async (cycleId, data = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await performanceCycleApi.launchCycle(cycleId, data);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to launch performance cycle');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    launchCycle,
    // React Query compatibility
    mutateAsync: launchCycle,
    isPending: loading,
    isError: !!error,
    error,
  };
};
